import { motion } from 'framer-motion';
import { UserPlus, ClipboardCheck, TrendingUp } from 'lucide-react';

const APP_SCREENSHOTS = {
  dashboard: '/app/dashboard-vazio.webp',
  sintomas: '/app/checklist-4-sintomas.webp',
  relatorios: '/app/relatorios-2-sintomas.webp',
};

const steps = [
  {
    number: '01',
    icon: UserPlus,
    title: 'Conte um pouco sobre você',
    description: 'Responda algumas perguntas rápidas sobre sua fase e seus sintomas. Em menos de 1 minuto, o MenoEase já vai entender sua jornada e personalizar tudo para você.',
    screenshot: APP_SCREENSHOTS.dashboard,
    alt: 'MenoEase Dashboard',
  },
  {
    number: '02',
    icon: ClipboardCheck,
    title: 'Registre seus sintomas',
    description: 'Use o checklist diário para rastrear humor, sono, sintomas e hábitos. Simples e rápido.',
    screenshot: APP_SCREENSHOTS.sintomas,
    alt: 'MenoEase Sintomas',
  },
  {
    number: '03',
    icon: TrendingUp,
    title: 'Receba insights',
    description: 'Obtenha relatórios detalhados, correlações entre sintomas e hábitos, e leve para seu médico.',
    screenshot: APP_SCREENSHOTS.relatorios,
    alt: 'MenoEase Relatórios',
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

export default function SolutionSection() {
  return (
    <section
      data-testid="solution-section"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #fafaf9 0%, #FFF5F8 50%, #fafaf9 100%)' }}
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FAADC7] to-transparent" />

      {/* Decorative blobs */}
      <div className="absolute top-20 right-0 w-72 h-72 blob-primary opacity-30" />
      <div className="absolute bottom-20 left-0 w-72 h-72 blob-light opacity-20" />

      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-20">
          <span className="text-sm font-semibold text-[#B54182] uppercase tracking-widest mb-4 block">
            Como funciona
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mb-6"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Simples. Rápido.{' '}
            <span className="text-[#B54182]">Transformador.</span>
          </h2>
          <p className="text-base md:text-lg text-stone-500">
            Em 3 passos simples, comece a entender seu corpo e retome o controle da sua saúde.
          </p>
        </motion.div>

        <div className="space-y-24 md:space-y-32">
          {steps.map((step, i) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                i % 2 !== 0 ? 'lg:direction-rtl' : ''
              }`}
            >
              {/* Text */}
              <div className={i % 2 !== 0 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[#FAADC7]/25 text-[#B54182]">
                    <step.icon size={26} />
                  </div>
                  <span className="text-5xl font-bold text-[#FAADC7]" style={{ fontFamily: 'var(--font-heading)' }}>
                    {step.number}
                  </span>
                </div>
                <h3
                  className="text-2xl sm:text-3xl font-bold text-stone-900 mb-4"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {step.title}
                </h3>
                <p className="text-base md:text-lg text-stone-500 leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Phone mockup */}
              <div className={`flex justify-center ${i % 2 !== 0 ? 'lg:order-1' : ''}`}>
                <motion.div
                  whileHover={{ y: -8, rotateY: 5 }}
                  transition={{ duration: 0.4 }}
                  className="phone-mockup w-52 sm:w-60"
                  style={{ perspective: '1000px' }}
                >
                  <img
                    src={step.screenshot}
                    alt={step.alt}
                    className="w-full"
                    loading="lazy"
                  />
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
